import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Leaf, LogOut, Package, Users, MousePointerClick, Plus, Pencil, Trash2, Download, ExternalLink, BarChart3, FileText, Tag, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = {
  name: "", brand: "", category: "cuidado-personal", subcategory: "", description: "", sello: "",
  image: "", price: "", currency: "USD", retailer: "Amazon", affiliate_url: "", tags: [], purity_score: 95, featured: false,
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-mono-tech uppercase tracking-widest text-emerald-400">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const inputCls = "h-10 w-full rounded-lg border border-border bg-secondary/40 px-3 text-sm outline-none focus:border-emerald-500/60";

const EMPTY_ARTICLE = {
  title: "", category: "cuidado-personal", excerpt: "", content: "", cover_image: "",
  author: "Equipo ZeroToxic", read_minutes: 5, published: true, featured: false, product_ids: [],
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [taxonomy, setTaxonomy] = useState({ lines: [], tags: [] });
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ products: 0, leads: 0, clicks: 0, articles: 0 });
  const [analytics, setAnalytics] = useState({ total_clicks: 0, top_products: [], by_category: [], by_tag: [] });
  const [analyticsDays, setAnalyticsDays] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [articles, setArticles] = useState([]);
  const [interestsSummary, setInterestsSummary] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [artFormOpen, setArtFormOpen] = useState(false);
  const [editingArt, setEditingArt] = useState(null);
  const [artForm, setArtForm] = useState(EMPTY_ARTICLE);
  const [savingArt, setSavingArt] = useState(false);
  const [deleteArtId, setDeleteArtId] = useState(null);

  const refresh = useCallback(() => {
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/articles").then(({ data }) => setArticles(data)).catch(() => {});
    api.get("/admin/interests-summary").then(({ data }) => setInterestsSummary(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/taxonomy").then(({ data }) => setTaxonomy(data));
    refresh();
  }, [refresh]);

  useEffect(() => {
    let params = {};
    if (customStart && customEnd) params = { start: customStart, end: customEnd };
    else if (analyticsDays !== "all") params = { days: parseInt(analyticsDays) };
    api.get("/admin/analytics", { params }).then(({ data }) => setAnalytics(data)).catch(() => {});
  }, [analyticsDays, customStart, customEnd]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, price: p.price ?? "", tags: p.tags || [] });
    setFormOpen(true);
  };

  const toggleFormTag = (tag) =>
    setForm((f) => ({ ...f, tags: f.tags.includes(tag) ? f.tags.filter((t) => t !== tag) : [...f.tags, tag] }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: form.price === "" ? null : parseFloat(form.price),
      purity_score: parseInt(form.purity_score) || 90,
    };
    delete payload.id;
    delete payload.slug;
    delete payload.created_at;
    try {
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Producto actualizado.");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Producto creado.");
      }
      setFormOpen(false);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/products/${deleteId}`);
      toast.success("Producto eliminado.");
      setDeleteId(null);
      refresh();
    } catch (err) {
      toast.error("No se pudo eliminar.");
    }
  };

  const openCreateArt = () => { setEditingArt(null); setArtForm(EMPTY_ARTICLE); setArtFormOpen(true); };
  const openEditArt = (a) => { setEditingArt(a); setArtForm({ ...a }); setArtFormOpen(true); };

  const saveArt = async (e) => {
    e.preventDefault();
    setSavingArt(true);
    const payload = { ...artForm, read_minutes: parseInt(artForm.read_minutes) || 5 };
    delete payload.id; delete payload.slug; delete payload.created_at;
    try {
      if (editingArt) {
        await api.put(`/admin/articles/${editingArt.id}`, payload);
        toast.success("Artículo actualizado.");
      } else {
        await api.post("/admin/articles", payload);
        toast.success("Artículo creado.");
      }
      setArtFormOpen(false);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar.");
    } finally {
      setSavingArt(false);
    }
  };

  const confirmDeleteArt = async () => {
    try {
      await api.delete(`/admin/articles/${deleteArtId}`);
      toast.success("Artículo eliminado.");
      setDeleteArtId(null);
      refresh();
    } catch (err) {
      toast.error("No se pudo eliminar.");
    }
  };

  const exportLeadsCSV = () => {
    const rows = [["email", "source", "interests", "created_at"], ...leads.map((l) => [l.email, l.source, (l.interests || []).join("|"), l.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "leads-zerotoxic.csv";
    a.click();
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const subOptions = taxonomy.lines.find((l) => l.id === form.category)?.subcategories || [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 glass">
        <div className="px-4 sm:px-8 max-w-7xl mx-auto flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 text-slate-950">
              <Leaf className="h-5 w-5" strokeWidth={2.4} />
            </span>
            <div>
              <div className="font-display font-extrabold text-sm">Panel del Curador</div>
              <div className="font-mono-tech text-[10px] text-muted-foreground">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/")} className="rounded-full">Ver sitio</Button>
            <Button data-testid="logout-btn" variant="outline" size="sm" onClick={doLogout} className="rounded-full">
              <LogOut className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="px-4 sm:px-8 max-w-7xl mx-auto py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Package, label: "Productos", value: stats.products, test: "stat-products" },
            { icon: FileText, label: "Guías", value: stats.articles, test: "stat-articles" },
            { icon: Users, label: "Leads capturados", value: stats.leads, test: "stat-leads" },
            { icon: MousePointerClick, label: "Clics de afiliado", value: stats.clicks, test: "stat-clicks" },
          ].map((s) => (
            <div key={s.label} data-testid={s.test} className="rounded-2xl border border-border bg-card p-6">
              <s.icon className="h-5 w-5 text-emerald-400" />
              <div className="mt-3 font-display text-3xl font-extrabold">{s.value}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="products">
          <TabsList className="mb-6">
            <TabsTrigger value="products" data-testid="tab-products">Productos</TabsTrigger>
            <TabsTrigger value="articles" data-testid="tab-articles">Guías</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analítica</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold">Catálogo ({products.length})</h2>
              <Button data-testid="add-product-btn" onClick={openCreate} className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-bold rounded-full">
                <Plus className="h-4 w-4 mr-1" /> Nuevo producto
              </Button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-muted-foreground">
                  <tr>
                    <th className="text-left p-4 font-medium">Producto</th>
                    <th className="text-left p-4 font-medium hidden md:table-cell">Línea</th>
                    <th className="text-left p-4 font-medium hidden lg:table-cell">Tags</th>
                    <th className="text-right p-4 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} data-testid={`admin-row-${p.id}`} className="border-t border-border hover:bg-secondary/30">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img src={p.image} alt="" className="h-10 w-10 rounded-lg object-cover" />
                          <div>
                            <div className="font-medium line-clamp-1">{p.name}</div>
                            <div className="text-xs text-muted-foreground">{p.brand}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell text-muted-foreground capitalize">{p.category.replace(/-/g, " ")}</td>
                      <td className="p-4 hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {p.tags.slice(0, 2).map((t) => <span key={t} className="rounded-full bg-secondary px-2 py-0.5 text-[10px]">{t}</span>)}
                          {p.tags.length > 2 && <span className="text-[10px] text-muted-foreground">+{p.tags.length - 2}</span>}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <button data-testid={`edit-btn-${p.id}`} onClick={() => openEdit(p)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary text-muted-foreground hover:text-emerald-400">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button data-testid={`delete-btn-${p.id}`} onClick={() => setDeleteId(p.id)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary text-muted-foreground hover:text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="articles">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold">Guías & artículos ({articles.length})</h2>
              <Button data-testid="add-article-btn" onClick={openCreateArt} className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-bold rounded-full">
                <Plus className="h-4 w-4 mr-1" /> Nueva guía
              </Button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-muted-foreground">
                  <tr>
                    <th className="text-left p-4 font-medium">Título</th>
                    <th className="text-left p-4 font-medium hidden md:table-cell">Línea</th>
                    <th className="text-left p-4 font-medium hidden lg:table-cell">Estado</th>
                    <th className="text-right p-4 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((a) => (
                    <tr key={a.id} data-testid={`admin-article-row-${a.id}`} className="border-t border-border hover:bg-secondary/30">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {a.cover_image ? <img src={a.cover_image} alt="" className="h-10 w-14 rounded-lg object-cover" /> : <div className="h-10 w-14 rounded-lg bg-secondary" />}
                          <div className="font-medium line-clamp-1 max-w-md">{a.title}</div>
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell text-muted-foreground capitalize">{a.category.replace(/-/g, " ")}</td>
                      <td className="p-4 hidden lg:table-cell">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] ${a.published ? "bg-emerald-500/15 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>
                          {a.published ? "Publicado" : "Borrador"}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <button data-testid={`edit-article-btn-${a.id}`} onClick={() => openEditArt(a)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary text-muted-foreground hover:text-emerald-400">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button data-testid={`delete-article-btn-${a.id}`} onClick={() => setDeleteArtId(a.id)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary text-muted-foreground hover:text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="analytics">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="rounded-2xl border border-border bg-card p-6 flex items-center gap-4 flex-1">
                <TrendingUp className="h-8 w-8 text-emerald-400" />
                <div>
                  <div className="font-display text-3xl font-extrabold" data-testid="analytics-total-clicks">{analytics.total_clicks}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Clics de afiliado {analyticsDays === "all" ? "totales" : `(últimos ${analyticsDays} días)`}</div>
                </div>
              </div>
              <Select value={analyticsDays} onValueChange={(v) => { setAnalyticsDays(v); setCustomStart(""); setCustomEnd(""); }}>
                <SelectTrigger data-testid="analytics-range-select" className="h-11 w-full sm:w-52 rounded-full bg-secondary/40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Últimos 7 días</SelectItem>
                  <SelectItem value="30">Últimos 30 días</SelectItem>
                  <SelectItem value="90">Últimos 90 días</SelectItem>
                  <SelectItem value="all">Todo el tiempo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mb-6 flex flex-wrap items-end gap-3 rounded-2xl border border-border bg-card p-4">
              <div>
                <label className="text-[11px] font-mono-tech uppercase tracking-widest text-emerald-400">Desde</label>
                <input data-testid="analytics-start" type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="mt-1 block h-10 rounded-lg border border-border bg-secondary/40 px-3 text-sm outline-none focus:border-emerald-500/60" />
              </div>
              <div>
                <label className="text-[11px] font-mono-tech uppercase tracking-widest text-emerald-400">Hasta</label>
                <input data-testid="analytics-end" type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="mt-1 block h-10 rounded-lg border border-border bg-secondary/40 px-3 text-sm outline-none focus:border-emerald-500/60" />
              </div>
              {(customStart || customEnd) && (
                <Button data-testid="analytics-clear-range" variant="outline" size="sm" onClick={() => { setCustomStart(""); setCustomEnd(""); }} className="rounded-full">Limpiar rango</Button>
              )}
              <p className="text-xs text-muted-foreground ml-auto self-center">Elige un rango exacto o usa los presets de arriba.</p>
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-mono-tech text-xs uppercase tracking-widest text-emerald-400 mb-4 flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Productos más clicados</h3>
                {analytics.top_products.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin datos aún. Los clics aparecerán cuando los visitantes usen "Comprar".</p>
                ) : (
                  <ul className="space-y-3" data-testid="analytics-top-products">
                    {analytics.top_products.map((p, i) => (
                      <li key={p.product_id} className="flex items-center justify-between gap-2">
                        <span className="text-sm line-clamp-1"><span className="text-muted-foreground mr-2">{i + 1}.</span>{p.name}</span>
                        <span className="font-mono-tech text-sm font-bold text-emerald-400">{p.clicks}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-mono-tech text-xs uppercase tracking-widest text-emerald-400 mb-4 flex items-center gap-2"><Package className="h-4 w-4" /> Clics por línea</h3>
                {analytics.by_category.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin datos aún.</p>
                ) : (
                  <ul className="space-y-3">
                    {analytics.by_category.map((c) => (
                      <li key={c.category} className="flex items-center justify-between gap-2">
                        <span className="text-sm capitalize">{c.category.replace(/-/g, " ")}</span>
                        <span className="font-mono-tech text-sm font-bold text-cyan-400">{c.clicks}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-mono-tech text-xs uppercase tracking-widest text-emerald-400 mb-4 flex items-center gap-2"><Tag className="h-4 w-4" /> Clics por etiqueta</h3>
                {analytics.by_tag.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin datos aún.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {analytics.by_tag.map((t) => (
                      <span key={t.tag} className="rounded-full bg-secondary border border-border px-3 py-1 text-xs">
                        {t.tag} <span className="font-bold text-emerald-400">{t.clicks}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="leads">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold">Leads capturados ({leads.length})</h2>
              <Button data-testid="export-leads-btn" onClick={exportLeadsCSV} variant="outline" className="rounded-full" disabled={leads.length === 0}>
                <Download className="h-4 w-4 mr-1" /> Exportar CSV
              </Button>
            </div>

            {interestsSummary.length > 0 && (
              <div data-testid="interests-summary" className="mb-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <p className="text-xs font-mono-tech uppercase tracking-widest text-emerald-400 mb-3">Segmentos por interés — para tus futuras novedades en el sitio</p>
                <div className="flex flex-wrap gap-2">
                  {interestsSummary.map((s) => (
                    <span key={s.category} className="rounded-full bg-secondary border border-border px-3 py-1 text-xs">
                      {s.label} <span className="font-bold text-emerald-400">{s.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {leads.length === 0 ? (
              <p className="text-center text-muted-foreground py-16 rounded-2xl border border-dashed border-border">Aún no hay leads. Aparecerán cuando alguien descargue la Guía.</p>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 text-muted-foreground">
                    <tr>
                      <th className="text-left p-4 font-medium">Email</th>
                      <th className="text-left p-4 font-medium hidden sm:table-cell">Origen</th>
                      <th className="text-left p-4 font-medium hidden md:table-cell">Intereses</th>
                      <th className="text-left p-4 font-medium">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-border hover:bg-secondary/30">
                        <td className="p-4 font-medium">{l.email}</td>
                        <td className="p-4 hidden sm:table-cell text-muted-foreground">{l.source}</td>
                        <td className="p-4 hidden md:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {(l.interests || []).length === 0 ? <span className="text-muted-foreground/50 text-xs">—</span> :
                              l.interests.map((it) => <span key={it} className="rounded-full bg-secondary px-2 py-0.5 text-[10px] capitalize">{it.replace(/-/g, " ")}</span>)}
                          </div>
                        </td>
                        <td className="p-4 text-muted-foreground">{new Date(l.created_at).toLocaleString("es-ES")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Product form */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar producto" : "Nuevo producto"}</DialogTitle>
            <DialogDescription>Completa los datos, asigna etiquetas de salud y el enlace de afiliado.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nombre"><input data-testid="form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
              <Field label="Marca"><input data-testid="form-brand" required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Línea">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, subcategory: "" })}>
                  <SelectTrigger data-testid="form-category" className="h-10 bg-secondary/40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {taxonomy.lines.map((l) => <SelectItem key={l.id} value={l.id}>{l.short}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Subcategoría">
                <Select value={form.subcategory || "none"} onValueChange={(v) => setForm({ ...form, subcategory: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="form-subcategory" className="h-10 bg-secondary/40"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Ninguna —</SelectItem>
                    {subOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="URL de imagen"><input data-testid="form-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputCls} placeholder="https://…" /></Field>
            <Field label="Descripción"><textarea data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:border-emerald-500/60" /></Field>
            <Field label="Sello ZeroToxic (por qué aprobó)"><textarea data-testid="form-sello" value={form.sello} onChange={(e) => setForm({ ...form, sello: e.target.value })} rows={2} className="w-full rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:border-emerald-500/60" /></Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Precio"><input data-testid="form-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} /></Field>
              <Field label="Retailer"><input value={form.retailer} onChange={(e) => setForm({ ...form, retailer: e.target.value })} className={inputCls} /></Field>
              <Field label="Pureza (%)"><input type="number" min="0" max="100" value={form.purity_score} onChange={(e) => setForm({ ...form, purity_score: e.target.value })} className={inputCls} /></Field>
            </div>
            <Field label="Enlace de afiliado (URL real)"><input data-testid="form-affiliate" value={form.affiliate_url} onChange={(e) => setForm({ ...form, affiliate_url: e.target.value })} className={inputCls} placeholder="https://amazon.com/…?tag=…" /></Field>
            <Field label="Etiquetas de salud">
              <div className="flex flex-wrap gap-2">
                {taxonomy.tags.map((t) => (
                  <button type="button" key={t} data-testid={`form-tag-${t}`} onClick={() => toggleFormTag(t)}
                    className={`rounded-full border px-2.5 py-1 text-xs transition-all ${form.tags.includes(t) ? "border-emerald-500 bg-emerald-500/20 text-emerald-300" : "border-border text-muted-foreground hover:border-emerald-500/50"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-emerald-500" />
              Destacar en la página de inicio
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
              <Button data-testid="form-save-btn" type="submit" disabled={saving} className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-bold">
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este producto?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Article form */}
      <Dialog open={artFormOpen} onOpenChange={setArtFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editingArt ? "Editar guía" : "Nueva guía"}</DialogTitle>
            <DialogDescription>Redacta el contenido y adjunta productos mencionados para impulsar clics de afiliado.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveArt} className="space-y-4">
            <Field label="Título"><input data-testid="art-title" required value={artForm.title} onChange={(e) => setArtForm({ ...artForm, title: e.target.value })} className={inputCls} /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Línea">
                <Select value={artForm.category} onValueChange={(v) => setArtForm({ ...artForm, category: v })}>
                  <SelectTrigger data-testid="art-category" className="h-10 bg-secondary/40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {taxonomy.lines.map((l) => <SelectItem key={l.id} value={l.id}>{l.short}</SelectItem>)}
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Autor"><input value={artForm.author} onChange={(e) => setArtForm({ ...artForm, author: e.target.value })} className={inputCls} /></Field>
            </div>
            <Field label="URL de imagen de portada"><input data-testid="art-image" value={artForm.cover_image} onChange={(e) => setArtForm({ ...artForm, cover_image: e.target.value })} className={inputCls} placeholder="https://…" /></Field>
            <Field label="Extracto (resumen)"><textarea data-testid="art-excerpt" value={artForm.excerpt} onChange={(e) => setArtForm({ ...artForm, excerpt: e.target.value })} rows={2} className="w-full rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:border-emerald-500/60" /></Field>
            <Field label="Contenido (separa párrafos con una línea en blanco)"><textarea data-testid="art-content" value={artForm.content} onChange={(e) => setArtForm({ ...artForm, content: e.target.value })} rows={8} className="w-full rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:border-emerald-500/60" /></Field>
            <Field label="Productos mencionados (aparecen al final de la guía)">
              <div className="max-h-40 overflow-y-auto rounded-lg border border-border bg-secondary/20 p-2 space-y-1">
                {products.filter((p) => artForm.category === "general" || p.category === artForm.category).map((p) => {
                  const checked = (artForm.product_ids || []).includes(p.id);
                  return (
                    <label key={p.id} data-testid={`art-product-${p.id}`} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-secondary cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setArtForm((f) => ({
                          ...f,
                          product_ids: checked ? f.product_ids.filter((x) => x !== p.id) : [...(f.product_ids || []), p.id],
                        }))}
                        className="h-4 w-4 accent-emerald-500"
                      />
                      <img src={p.image} alt="" className="h-7 w-7 rounded object-cover" />
                      <span className="line-clamp-1">{p.name}</span>
                    </label>
                  );
                })}
                {products.filter((p) => artForm.category === "general" || p.category === artForm.category).length === 0 && (
                  <p className="text-xs text-muted-foreground px-2 py-1">No hay productos en esta línea.</p>
                )}
              </div>
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Minutos de lectura"><input type="number" min="1" value={artForm.read_minutes} onChange={(e) => setArtForm({ ...artForm, read_minutes: e.target.value })} className={inputCls} /></Field>
              <div className="flex flex-col justify-end gap-2 pb-1">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={artForm.published} onChange={(e) => setArtForm({ ...artForm, published: e.target.checked })} className="h-4 w-4 accent-emerald-500" />
                  Publicado (visible en el sitio)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input data-testid="art-featured" type="checkbox" checked={artForm.featured} onChange={(e) => setArtForm({ ...artForm, featured: e.target.checked })} className="h-4 w-4 accent-emerald-500" />
                  Destacar (aparece en grande en Blog y Home)
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setArtFormOpen(false)}>Cancelar</Button>
              <Button data-testid="art-save-btn" type="submit" disabled={savingArt} className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-bold">
                {savingArt ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteArtId} onOpenChange={(v) => !v && setDeleteArtId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta guía?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-article-btn" onClick={confirmDeleteArt} className="bg-red-500 hover:bg-red-600 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
